import { mapValues } from "lodash"

import { InboxRecommendation, type InboxRecommendationType } from "~/constants/npi"

import type { Rule } from "./types"

export const rules: Record<InboxRecommendationType, Rule> = {
  [InboxRecommendation.ReviewActivation]: {
    id: InboxRecommendation.ReviewActivation,
    explanation:
      "NOOS variant whose activation date is over 6 months old and still has no sales-based forecast.",
    customerSummary:
      "New NOOS variant activated over 6 months ago, but Hakio still has no sales of its own to forecast from.",
    whatToDo:
      "Check the activation date is right, or update it so Hakio recalculates the forecast.",
    whyItMatters:
      "Until real sales come in, the plan rests on the activation date alone. A stale date means a stale forecast.",
  },
  [InboxRecommendation.SetActivation]: {
    id: InboxRecommendation.SetActivation,
    explanation:
      "NOOS variant already in a NOOS collection but missing an activation date, so Hakio can't forecast it.",
    customerSummary:
      "New NOOS variant in a collection but with no activation date. With no sales of its own yet, Hakio doesn't know when to start planning it.",
    whatToDo: "Set an activation date to switch on forecasting.",
    whyItMatters: "Without one it gets no forecast and stays out of your plans.",
  },
  [InboxRecommendation.PhaseOut]: {
    id: InboxRecommendation.PhaseOut,
    explanation: "Variant sold in the past but had no sales in the last 12 months.",
    customerSummary: "Variant sold before but hasn't sold anything in the last 12 months.",
    whatToDo: "Phase it out if you're done with it, or add it to a collection if you'll restock.",
    whyItMatters: "Leaving dead variants active clutters your plans and distorts demand.",
  },
  [InboxRecommendation.AddToCollection]: {
    id: InboxRecommendation.AddToCollection,
    explanation:
      "Variant has activity (stock, incoming orders, pre-orders, or sales) but isn't in any collection yet.",
    customerSummary:
      "Variant has stock, incoming orders, pre-orders, or past sales, but isn't in any collection.",
    whatToDo: "Add it to a collection so Hakio starts forecasting it.",
    whyItMatters: "Anything outside a collection is invisible to your plans and ordering.",
  },
  [InboxRecommendation.HandleInErp]: {
    id: InboxRecommendation.HandleInErp,
    explanation:
      "Org manages collections in the ERP and this variant isn't in any collection there.",
    customerSummary:
      "Your collections are managed in your ERP, and this variant isn't in a collection there.",
    whatToDo: "Add the variant to a collection in your ERP. Hakio syncs on the next ingest.",
    whyItMatters: "Until it's fixed in the ERP, Hakio can't forecast or plan this variant.",
  },
  [InboxRecommendation.ReviewSystemDate]: {
    id: InboxRecommendation.ReviewSystemDate,
    explanation:
      "Activation date was carried over from a prior calculation (system-set) and hasn't been user-reviewed.",
    customerSummary:
      "The activation date was carried over from a prior calculation and hasn't been reviewed yet.",
    whatToDo:
      "Open and save the activation date, with or without changes, to confirm it. That marks it user-reviewed and removes it from the inbox.",
    whyItMatters:
      "An unreviewed system-set date may not reflect when the variant actually went live, so its forecast may start from the wrong week.",
  },
}

export type RuleCopy = Pick<Rule, "customerSummary" | "whatToDo" | "whyItMatters">

export const ruleCopy: Record<InboxRecommendationType, RuleCopy> = mapValues(rules, (rule) => ({
  customerSummary: rule.customerSummary,
  whatToDo: rule.whatToDo,
  whyItMatters: rule.whyItMatters,
}))
